import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { type KeyboardEvent, useRef, useState } from 'react';
import { stripHtml } from '@libs/common-next';
import type { AssistedSearchState, SearchItem } from '../search.types';
const EMPTY_ASSISTED_SEARCH: AssistedSearchState = {
  searchTerm: '',
  searchAssistTerm: '',
  searchAssistName: '',
  isModified: false
};
function getSearchAssistName(item: SearchItem, query: string) {
  if (item.kind === 'recent') return 'recent searches';
  if (item.kind === 'product') return 'product results';
  return query === '' ? 'popular searches' : '';
}
export function useSearchQueryState() {
  const $ = _c(15);
  const params = useSearchParams();
  let t0;
  if ($[0] !== params) {
    t0 = stripHtml(params?.get("q") ?? "");
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [query, setQueryState] = useState(t0);
  const currentQueryRef = useRef(query);
  const [assistedSearch, setAssistedSearch] = useState(EMPTY_ASSISTED_SEARCH);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = nextQuery => {
      currentQueryRef.current = nextQuery;
      setQueryState(nextQuery);
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setQuery = t1;
  let t2;
  if ($[3] !== assistedSearch) {
    t2 = event => {
      if (event.key === "Enter") {
        return;
      }
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        return;
      }
      const inputValue = event.currentTarget.value;
      if (inputValue === "") {
        setAssistedSearch(EMPTY_ASSISTED_SEARCH);
      } else {
        if (assistedSearch.searchTerm !== "" && assistedSearch.searchTerm !== inputValue) {
          setAssistedSearch(_temp);
        }
      }
    };
    $[3] = assistedSearch;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onInputKeyDown = t2;
  let t3;
  if ($[5] !== query) {
    t3 = item => {
      setAssistedSearch({
        searchTerm: item.label,
        searchAssistTerm: query,
        searchAssistName: getSearchAssistName(item, query),
        isModified: false
      });
    };
    $[5] = query;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const captureSearchSelection = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = item_0 => {
      if (item_0.kind !== "product") {
        setQuery(item_0.label);
      }
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const completeSearchSelection = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setQuery("");
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const clearQuery = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => currentQueryRef.current;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== assistedSearch || $[11] !== captureSearchSelection || $[12] !== onInputKeyDown || $[13] !== query) {
    t7 = {
      assistedSearch,
      captureSearchSelection,
      clearQuery,
      completeSearchSelection,
      getCurrentQuery: t6,
      onInputKeyDown,
      query,
      setQuery
    };
    $[10] = assistedSearch;
    $[11] = captureSearchSelection;
    $[12] = onInputKeyDown;
    $[13] = query;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function _temp(current) {
  return {
    ...current,
    isModified: true
  };
}